import { useState } from 'react'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import {
  HouseStudyDayIndividualFormContainer,
  HouseStudyDayIndividualFormContainerProps,
} from './components/components/form-container/HouseStudyDayIndividualFormContainer'
import { HouseStudyDayIndividualIsEnabledField } from './components/components/is-enabled-field/HouseStudyDayIndividualIsEnabledField'
import { HouseStudyDayIndividualOptionsFields } from './components/components/options-fields/HouseStudyDayIndividualOptionsFields'
import {
  computeHouseStudyDayIndividualDefaultValues,
  computeHouseStudyDayIndividualInputs,
} from './helpers/compute-house-study-day-individual'
import { HouseStudyDayIndividualDto } from '../../../../../../lib/graphql'
import { HouseOptionsFormLayoutProps } from '../../../HouseOptionsForm'

export type HouseStudyDayIndividualFormProps = {
  updateStudyDayIndividual: {
    mutate: (formData: HouseStudyDayIndividualDto) => void
    isLoading: boolean
  }
}
export const HouseStudyDayIndividualForm = ({
  handleOnExpand,
  house,
  isDisabled,
  isOpen,
  layoutProps,
  updateStudyDayIndividual,
}: HouseStudyDayIndividualFormProps & HouseOptionsFormLayoutProps) => {
  const [hasRequestBeenSent, setHasRequestBeenSent] = useState<boolean>(false)

  const { currencyArea, options } = house

  // Infer GQL Union type
  if (options.__typename !== 'HouseOptionsIndividual') {
    return null
  }

  const { studyDay } = options

  const methods = useForm<HouseStudyDayIndividualDto>({
    defaultValues: computeHouseStudyDayIndividualDefaultValues(studyDay),
  })

  const {
    control,
    watch,
    reset,
    formState: { isSubmitting, isValid, isDirty },
    handleSubmit,
  } = methods

  const { mutate } = updateStudyDayIndividual

  const isEnabled = watch('isEnabled')

  const timeOutRequest = () => {
    setHasRequestBeenSent(true)
    setTimeout(() => setHasRequestBeenSent(false), 2000)
  }

  const onSubmit = (formData: HouseStudyDayIndividualDto) => {
    const inputs = computeHouseStudyDayIndividualInputs({ ...formData })

    mutate({ ...inputs })

    reset(formData)

    timeOutRequest()
  }

  const isFieldDisabled = isDisabled || hasRequestBeenSent

  const formContainerProps: Omit<
    HouseStudyDayIndividualFormContainerProps,
    'children'
  > = {
    handleOnExpand,
    isOpen,
    layoutProps,
    submitButtons: {
      confirm: hasRequestBeenSent,
      disabled: isFieldDisabled || !isDirty || !isValid,
      isLoading: isSubmitting || updateStudyDayIndividual.isLoading,
      onSubmit: handleSubmit(onSubmit),
      isValid,
    },
  }

  return (
    <FormProvider {...methods}>
      <HouseStudyDayIndividualFormContainer {...formContainerProps}>
        <HouseStudyDayIndividualIsEnabledField
          control={control}
          isFieldDisabled={isFieldDisabled}
        />

        {isEnabled ? (
          <HouseStudyDayIndividualOptionsFields
            control={control}
            currencyArea={currencyArea}
            isFieldDisabled={isFieldDisabled}
          />
        ) : null}
      </HouseStudyDayIndividualFormContainer>
    </FormProvider>
  )
}
